import Styles from './Images.module.scss'
import { collectResults } from 'utils/query'
import { Button } from 'blaze-ui/Button'
import { Grid } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { useInfiniteBrandKitMediaFiles } from 'hooks/queries/brandKits/useInfiniteBrandKitMediaFiles'
import { useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import {
  ACCEPTED_IMAGE_MIME_TYPES,
  ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
} from 'screens/Finder/components/views/GalleryView/constants'
import { useModals } from 'almanac-ui/Modal'
import { AlmanacFile, SelectedImage } from 'types'
import { AllImages } from 'components/Wizards/DesignWizard/AddPhoto/SelectImage/AllImages'
import { DragOverlayForFirstUpload } from 'components/Design/UploadsTab/DragOverlay'
import { useDropzone } from 'react-dropzone'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCurrentImageLibrary } from 'hooks/useCurrentImageLibrary'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { ShimmeringColor } from 'components/BrandWizardVisualAnalysisModal/LoadingState'
import { useState } from 'react'
import { BrandKitField } from 'components/Wizards/DesktopWizards/Fields/BrandKitField/BrandKitField'
import { MediaFilePreview } from './MediaFilePreview'
import { getMediaFiles } from 'components/Design/UploadsTab/getMediaFiles'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'

interface Props {
  brandKitId: number
  onSelectImage: (value: SelectedImage) => void
  lastSelectedImage?: SelectedImage
  onSelectImageFromAll?: (image: SelectedImage) => void
  isDisabled?: boolean
  onBrandKitChange?: (brandKitId: number) => void
}

export const BrandKitImages = ({
  brandKitId,
  lastSelectedImage,
  onSelectImage,
  onSelectImageFromAll,
  isDisabled,
  onBrandKitChange,
}: Props) => {
  const imagePickerContext = useImagePickerContext()
  const [selectedBrandKitId, setSelectedBrandKitId] = useState<number>(brandKitId)
  const { selectedImages, accept } = imagePickerContext
  const mediaFilesQuery = useInfiniteBrandKitMediaFiles({ brandKitId: selectedBrandKitId, items: 4 })
  const mediaFiles = getMediaFiles(
    collectResults(mediaFilesQuery, (data) => data.media_files) as AlmanacFile[],
    accept || ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
  )
  const mediaFilesAsSelectedFiles: SelectedImage[] = mediaFiles
    .map((mf) => ({ ...mf, url: mf.source_url as string }))
    .slice(0, 4)
  const { openModal, closeModal } = useModals()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const imageLibrary = useCurrentImageLibrary()
  const isVirtualMarketer = useCurrentWorkspace()?.is_virtual_marketer

  const { uploadImageFiles, isUploading } = useImportMediaFiles(undefined, () => {
    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', selectedBrandKitId, 'mediaFiles'])
    if (imageLibrary?.key) {
      queryClient.invalidateQueries(['workspace', workspaceId, 'files', imageLibrary.key])
    }
  })

  const handleDrop = (files: File[]) => uploadImageFiles(files, imageLibrary?.key, { brand_kit_id: selectedBrandKitId })

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    accept: accept || ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
  })

  const handleBrandKitChange = (brandKit?: number) => {
    if (brandKit) {
      setSelectedBrandKitId(brandKit)
      onBrandKitChange?.(brandKit)
    }
  }

  const handleOpenAll = () => {
    openModal(
      AllImages,
      {
        ...imagePickerContext,
        accept: accept || ACCEPTED_IMAGE_MIME_TYPES,
        confirmLabel: 'Use Image',
        onPressOutside: closeModal,
        onConfirm: (images) => {
          onSelectImage(images[0])
          closeModal()
          onSelectImageFromAll?.(images[0])
        },
        initialBrandKitId: selectedBrandKitId,
      },
      { newStack: true },
    )
  }

  if (mediaFilesQuery.isLoading || mediaFilesQuery.isFetching)
    return (
      <div className={Styles.imagesSection}>
        <div className={Styles.header}>
          <Text variant="largeList">or from your brand kit</Text>
          <div className={Styles.brandKitInputContainer}>
            <BrandKitField
              size="sm"
              onChange={handleBrandKitChange}
              value={selectedBrandKitId}
              isDisabled
              allowCreate={!isVirtualMarketer}
            />
          </div>
        </div>
        <div className={Styles.imagesCard} {...getRootProps()}>
          <div className={Styles.imagesGrid}>
            {Array.from({ length: 4 }).map((_, i) => (
              <ShimmeringColor aspectRatio={1} width={152} height={152} key={i} />
            ))}
          </div>
          <Button frontIcon={Grid} variant="tertiary" isDisabled>
            Select from all Brand Kits or Uploads
          </Button>
        </div>
      </div>
    )

  return (
    <div className={Styles.imagesSection}>
      <div className={Styles.header}>
        <Text variant="largeList">or from your brand kit</Text>
        <div className={Styles.brandKitInputContainer}>
          <BrandKitField
            size="sm"
            onChange={handleBrandKitChange}
            value={selectedBrandKitId}
            allowCreate={!isVirtualMarketer}
          />
        </div>
      </div>
      <div className={Styles.imagesCard} {...getRootProps()} data-testid="upload-dropzone">
        {mediaFiles.length > 0 && (
          <>
            <div className={Styles.imagesGrid}>
              {mediaFilesAsSelectedFiles.map((file) => (
                <MediaFilePreview
                  key={file.id}
                  mediaFile={file}
                  isDisabled={isDisabled}
                  isSelected={file.id === selectedImages[0]?.id}
                  alt="uploaded to brand kit"
                  showVideoPreview={false}
                />
              ))}
              {Array.from({ length: 4 - mediaFilesAsSelectedFiles.length }).map((_, i) => (
                <div key={i} className={Styles.emptyPlaceholder} />
              ))}
            </div>
            <Button frontIcon={Grid} onPress={handleOpenAll} variant="tertiary" isDisabled={isDisabled}>
              Select from all Brand Kits or Uploads
            </Button>
          </>
        )}
        {mediaFiles.length === 0 && (
          <>
            <DragOverlayForFirstUpload
              style={{
                minHeight: 314,
                width: 314,
                margin: 0,
                border: '3px dashed var(--dark-15, rgba(0, 0, 0, 0.15))',
                boxShadow: 'none',
              }}
              active={isDragActive}
              withSupportedFilesTag={false}
              buttonProps={{
                children: isUploading ? 'Uploading...' : 'Uploads Files',
                size: 'md',
                variant: 'primary',
                onPress: open,
                isDisabled: isUploading,
              }}
              button={Button}
            />
            <input {...getInputProps()} />
            <Button frontIcon={Grid} onPress={handleOpenAll} variant="tertiary">
              Select from all Brand Kits or Uploads
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
